/* <bedrock-slider> — the theme's one carousel standard (BUILD-GUIDE §7).
   Subclasses Dawn's SliderComponent (global.js, loaded before this file) and adds:
     • a scroll progress bar (generic hook: .bedrock-slider__progress-fill — sections keep
       their own BEM class for styling and add the hook class alongside);
     • opt-in pointer drag-to-scroll        (`drag` attribute);
     • opt-in horizontal wheel scrolling    (`wheel` attribute).

   Both extras are attribute-gated so existing tracks are untouched, and both work WITH
   native scroll-snap rather than replacing it: they suspend snapping for the duration of
   the gesture and drop it again on release, at which point the browser settles onto the
   nearest cell by itself. No transforms, so SliderComponent's scroll-derived page math
   (and the progress bar below) keep reading the truth off scrollLeft.

   SliderComponent's constructor returns early when there are no prev/next buttons, so the
   progress bar binds its own listeners in that case. */

if (!customElements.get('bedrock-slider')) {
  /* Inside the guard, not at top level: several sections load this file, so it
     executes once per <script> tag — top-level consts redeclare and the whole
     second run dies with a SyntaxError in the console (2026-08-17). */
  const BEDROCK_SLIDER_DRAG_THRESHOLD = 3;
  const BEDROCK_SLIDER_GESTURE_END_DELAY = 120;

  customElements.define(
    'bedrock-slider',
    class BedrockSlider extends SliderComponent {
      constructor() {
        super();
        if (!this.slider) return;

        this.progressFill = this.querySelector('.bedrock-slider__progress-fill');

        if (this.progressFill) {
          if (!this.nextButton) {
            this.slider.addEventListener('scroll', this.updateProgress.bind(this));
            new ResizeObserver(() => this.updateProgress()).observe(this.slider);
          }
          this.updateProgress();
        }

        if (this.hasAttribute('drag')) this.initDrag();
        if (this.hasAttribute('wheel')) this.initWheel();
      }

      update() {
        super.update();
        this.updateProgress();
      }

      updateProgress() {
        if (!this.progressFill || !this.slider) return;
        const scrollWidth = this.slider.scrollWidth;
        const ratio =
          scrollWidth > 0 ? (this.slider.scrollLeft + this.slider.clientWidth) / scrollWidth : 1;
        this.progressFill.style.transform = `scaleX(${Math.min(Math.max(ratio, 0), 1)})`;
      }

      /* Pointer drag. Touch is left to native scrolling — it is smoother than anything
         re-implemented here, and pointercapture would swallow the browser's own panning. */
      initDrag() {
        const slider = this.slider;
        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let tracking = false;
        let dragged = false;
        let swallowNextClick = false;

        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.button !== 0) return;
          // Below the carousel breakpoint the track is a plain grid with nothing to
          // scroll. Tracking there would still cross the drag threshold and swallow the
          // click that follows, so a link inside the cell would stop opening.
          if (slider.scrollWidth <= slider.clientWidth) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = slider.scrollLeft;
          tracking = true;
          dragged = false;
          swallowNextClick = false;
        });

        slider.addEventListener('pointermove', (event) => {
          if (!tracking || event.pointerId !== pointerId) return;
          const delta = event.clientX - startX;

          // Below the threshold this is still a click, not a drag — stay out of the way.
          if (!dragged) {
            if (Math.abs(delta) < BEDROCK_SLIDER_DRAG_THRESHOLD) return;
            dragged = true;
            slider.classList.add('bedrock-slider--dragging');
            slider.setPointerCapture(pointerId);
          }

          event.preventDefault();
          slider.scrollLeft = startScrollLeft - delta;
        });

        const endDrag = (event) => {
          if (!tracking || (event.pointerId != null && event.pointerId !== pointerId)) return;
          tracking = false;

          if (dragged) {
            // A drag must not also open the link it finished on.
            swallowNextClick = true;
            if (slider.hasPointerCapture(pointerId)) slider.releasePointerCapture(pointerId);
            // Restoring scroll-snap makes the browser settle onto the nearest cell.
            slider.classList.remove('bedrock-slider--dragging');
          }

          pointerId = null;
          dragged = false;
        };

        slider.addEventListener('pointerup', endDrag);
        slider.addEventListener('pointercancel', endDrag);

        slider.addEventListener(
          'click',
          (event) => {
            if (!swallowNextClick) return;
            swallowNextClick = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
      }

      /* Horizontal wheel / trackpad only — Swiper's forceToAxis semantics. A vertical
         gesture is left alone so the page still scrolls over the strip. */
      initWheel() {
        const slider = this.slider;
        let settleTimer = null;

        slider.addEventListener(
          'wheel',
          (event) => {
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
            if (slider.scrollWidth <= slider.clientWidth) return;

            event.preventDefault();
            slider.classList.add('bedrock-slider--wheeling');
            slider.scrollLeft += event.deltaX;

            clearTimeout(settleTimer);
            settleTimer = setTimeout(() => {
              slider.classList.remove('bedrock-slider--wheeling');
            }, BEDROCK_SLIDER_GESTURE_END_DELAY);
          },
          { passive: false }
        );
      }
    }
  );
}
